import { useAtom } from "jotai";
import classNames from "classnames";
import { toast } from "react-hot-toast";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import Button from "components/base/Button";
import { dateFormatter, formatErrorForReact } from "helpers/utils";
import { loadStripe } from "@stripe/stripe-js";
import { Card, Col, Row } from "react-bootstrap";
import { user_info } from "jotai/UserJotaiInstance";
import { Fragment, useEffect, useState } from "react";
import { internalPricingItems } from "data/landing/pricingData";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import PricingItemInternal from "components/modules/landing/default/pricing/PricingItemInternal";

const Subscription = () => {
  const [cookie, setCookie] = useCookies(["isLoggedInKIQ", "stripeIdpKey"]);

  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState<any>(null);

  const [, setUserInfo] = useAtom(user_info);

  const stripePromise = loadStripe(
    process.env.REACT_STRIPE_PUB_KEY as string
  );

  useEffect(() => {
    axiosInstance.get("/users/user_subscription").then((res) => {
      setUserSubscription(res.data.data.subscription);
    });
  }, []);

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      if (!userSubscription?.stripe_subscription_id) {
        throw new Error('No active subscription found');
      }
      
      const response = await axiosInstance.delete(
        `/users/user_subscription/${userSubscription.stripe_subscription_id}`
      );

      if (response.data.statusCode === 200) {
        toast.success("Subscription cancelled successfully");
        setUserSubscription(response.data.data.subscriptionData);
        const userResponse = await axiosInstance.get("/users/me");
        setUserInfo(userResponse.data.data.authenticatedUser);
      }

      setShowDeleteModal(false);
    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
    } finally {
      setLoading(false);
    }
  };

  const handleBuySubscription = async (type: string, interval: string) => {
    try {
      let idempotency_key = cookie?.stripeIdpKey;
    
      if (!idempotency_key) {
        idempotency_key = crypto?.randomUUID();
        setCookie('stripeIdpKey', idempotency_key, {
          path: '/',
          maxAge: 86400 // 24 hours
        });
      }
      const response = await axiosInstance.post("/users/renew_subscription", {
        plan_name: type,
        interval: interval,
        idempotency_key
      });

      const stripe = await stripePromise;

      const result = await stripe?.redirectToCheckout({
        sessionId: response.data.id,
      });
      if (result?.error) {
        console.log(result.error);
      }
    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
    }
  };

  // const isActive = userSubscription?.subscription_status === "Active";
  const isTrial = userSubscription?.subscription_status === "FreeTrial" && userSubscription?.free_trial_days_remaining > 0;
  const isPaid = userSubscription?.user?.payment_status === "Paid" && userSubscription?.free_trial_days_remaining <= 0;
  const isCancelled = userSubscription?.subscription_status === "Cancelled";

  return (
    <Fragment>
      <Row className="g-4">
        <Col xs={12} xl={12}>
          <Row className="g-3">
            <Col xs={12} sm={12}>
              {isTrial && (
                  <h4>
                    You're enjoying the {userSubscription.subscription_package?.subscription_package_name} package with{" "}
                    {userSubscription.free_trial_days_remaining} days left in your free trial. Enjoy all the features while they last!
                  </h4>
                )}

              {isPaid && (
                  <h4>
                    You're enjoying the {userSubscription.subscription_package?.subscription_package_name}. You can cancel it if you want to upgrade the subscription.
                  </h4>
                )}

              {isCancelled && (
                <p>
                  Your subscription was cancelled on{" "}
                  {userSubscription.cancelled_at ? dateFormatter(userSubscription.cancelled_at) : 'N/A'}
                </p>
              )}
            </Col>
            <Col xs={12} sm={6}>
              {userSubscription?.subscription_status !== "Cancelled" ? (
                <Button
                  variant="danger"
                  className="w-100 mb-2"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Cancel subscription
                </Button>
              ) : (
                <h3 className="mb-0">Start subscription again</h3>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {userSubscription?.subscription_status === "Cancelled" && (
        <Row className="pt-3 g-3 g-xl-0 mb-4 mb-md-8">
          {internalPricingItems.map((item: any, index: any) => (
            <Col key={item.id} md={4} xl={4}>
              <Col>
                <Card
                  className={classNames("h-100", {
                    "border border-2 border-primary mt-5 mt-md-0 rounded-start-0":
                      item.popular,
                    "rounded-end-xl-0": index === 0,
                    "rounded-end-xl-0 rounded-start-0": index === 1,
                    "rounded-start-0": index === 2,
                  })}
                >
                  {item.popular && (
                    <div className="position-absolute d-flex flex-center bg-primary-subtle rounded-top py-1 end-0 start-0 badge-pricing">
                      <p className="text-primary-dark mb-0">Most popular</p>
                    </div>
                  )}
                  <Card.Body className="px-2">
                    <PricingItemInternal
                      pricing={item}
                      handleBuySubscription={handleBuySubscription}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Col>
          ))}
        </Row>
      )}

      {loading && <div className="loading-overlay"></div>}

      <ConfirmDeleteModal
        modalShow={showDeleteModal}
        setModalShow={setShowDeleteModal}
        message="Yes, Cancel it!"
        loading={loading}
        handleDelete={() => handleCancelSubscription()}
      />
    </Fragment>
  );
};

export default Subscription;
